import { TextSearchIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasTextArea,
  FieldSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  TextSimilarityShape,
  textSimilarityType,
  ComparisonOperator,
  comparisonOperators,
  fields,
  TextSimilarityProps,
  Dimensions,
  Field,
  metadataType,
  textOperators,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isTextSimilarityShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is TextSimilarityShape {
  return shape.type === textSimilarityType;
}

export class TextSimilarityTool extends ToolStateNode {
  static override id = textSimilarityType;

  static override uiToolDef = (editor: Editor) => ({
    id: textSimilarityType,
    icon: TextSearchIcon,
    label: TextSimilarityShapeUtil.docs.title,
    defaultColor: TextSimilarityShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(textSimilarityType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={TextSimilarity}
        util={TextSimilarityShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: textSimilarityType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class TextSimilarityShapeUtil extends BindableUtil<TextSimilarityShape> {
  static override migrations = createMigrations(textSimilarityType);
  static override type = textSimilarityType;

  static override docs = {
    defaultColor: "pink" as const,
    title: "Text Similarity",
    description: (
      <div className="flex flex-col gap-2">
        <div>
          Evaluates the similarity between the text in an attribute and a test
          string.
        </div>
      </div>
    ),
  };

  override getType() {
    return textSimilarityType;
  }

  static override props: RecordProps<TextSimilarityShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    anchorText: T.string,
    modelName: T.string,
    threshold: T.number,
    operator: T.literalEnum(...comparisonOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 400,
      h: 173,
      var: fields[0],
      anchorText: "",
      modelName: "all-mpnet-base-v2",
      threshold: 0.75,
      operator: ">=" as const,
      metadata: undefined,
    };
  }

  override component(shape: TextSimilarityShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <TextSimilarity
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function TextSimilarity({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: TextSimilarityShape;
  className?: string;
  updateShape: (
    shape: TextSimilarityShape,
    props: Partial<TextSimilarityProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          TextSimilarityShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={TextSimilarityShapeUtil.docs}
        />

        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <CanvasTextArea
              value={shape.props.anchorText}
              placeholder="Text to compare to (250 tokens max)"
              maxLength={1000}
              onValueChange={(anchorText) => updateShape(shape, { anchorText })}
            />

            <div className="flex gap-2">
              <FieldSelect
                value={shape.props.var}
                validOperators={textOperators}
                onValueChange={(v) => updateShape(shape, { var: v as Field })}
              />
              <PercentOperatorSelect
                value={shape.props.operator}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as ComparisonOperator })
                }
              />
              <SimpleTooltip message="The similarity threshold.">
                <CanvasPercentageInput
                  aria-label="Threshold"
                  value={shape.props.threshold}
                  placeholder="Threshold"
                  className="flex-1"
                  onValueChange={(value) =>
                    updateShape(shape, { threshold: value })
                  }
                />
              </SimpleTooltip>
            </div>
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
